import React, { useState } from 'react';
import axios from 'axios';
import { Phone, MapPin, CheckCircle, AlertCircle } from 'lucide-react';
import { API_BASE } from '../utils/apiBase';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    clinic_location: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const clinics = [
    {
      name: 'Electronic City Clinic',
      location: 'Electronic City, Bangalore',
      phones: ['+91 91084 24084', '+91 96320 74737']
    },
    {
      name: 'Koramangala Clinic',
      location: 'Koramangala, Bangalore',
      phones: ['+91 91085 76094', '+91 91089 86094']
    },
    {
      name: 'Marathahalli Clinic',
      location: 'Marathahalli, Bangalore',
      phones: ['+91 91087 36094', '+91 99801 04737']
    }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API_BASE}/contact`, formData);
      setSubmitStatus({ type: 'success', message: 'Thank you! We will contact you soon.' });
      setFormData({
        name: '',
        email: '',
        phone: '',
        clinic_location: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({ 
        type: 'error', 
        message: 'Failed to submit form. Please try again or call us directly.' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="bg-cream py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-5xl font-bold text-center text-dark-text mb-6" data-testid="contact-heading">
            Contact Us
          </h1>
          <p className="text-xl text-center text-gray-700 max-w-3xl mx-auto">
            Book a consultation or reach out to us at any of our three clinic locations in Bangalore.
          </p>
        </div>
      </section>

      {/* Contact Form and Clinics */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-dark-text mb-6" data-testid="contact-form-heading">
                Send Us a Message
              </h2>
              
              {submitStatus && (
                <div 
                  className={`p-4 rounded-lg mb-6 flex items-center ${
                    submitStatus.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                  }`}
                  data-testid="contact-form-status"
                >
                  {submitStatus.type === 'success' ? (
                    <CheckCircle size={20} className="mr-2" />
                  ) : (
                    <AlertCircle size={20} className="mr-2" />
                  )}
                  {submitStatus.message}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-dark-text mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    data-testid="contact-form-name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden focus:border-golden"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-dark-text mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    data-testid="contact-form-email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden focus:border-golden"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-dark-text mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    data-testid="contact-form-phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden focus:border-golden"
                  />
                </div>

                <div>
                  <label htmlFor="clinic_location" className="block text-sm font-semibold text-dark-text mb-2">
                    Preferred Clinic Location *
                  </label>
                  <select
                    id="clinic_location"
                    name="clinic_location"
                    data-testid="contact-form-clinic"
                    value={formData.clinic_location}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden focus:border-golden"
                  >
                    <option value="">Select a clinic</option>
                    <option value="Electronic City">Electronic City</option>
                    <option value="Koramangala">Koramangala</option>
                    <option value="Marathahalli">Marathahalli</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-dark-text mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    data-testid="contact-form-message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golden focus:border-golden"
                    placeholder="Tell us about your health concerns or questions..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-form-submit"
                  className="w-full btn-primary px-6 py-4 rounded-lg font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Clinic Locations */}
            <div>
              <h2 className="text-3xl font-bold text-dark-text mb-6" data-testid="clinics-heading">
                Our Clinic Locations
              </h2>
              <div className="space-y-6">
                {clinics.map((clinic, index) => (
                  <div 
                    key={index} 
                    className="bg-cream p-6 rounded-lg"
                    data-testid={`clinic-${index}`}
                  >
                    <h3 className="text-xl font-bold text-golden mb-3">{clinic.name}</h3>
                    <div className="space-y-2">
                      <div className="flex items-start text-gray-700">
                        <MapPin size={20} className="mr-2 mt-1 text-golden flex-shrink-0" />
                        <span>{clinic.location}</span>
                      </div>
                      {clinic.phones.map((phone, phoneIndex) => (
                        <a
                          key={phoneIndex}
                          href={`tel:${phone.replace(/\s/g, '')}`}
                          className="flex items-center text-gray-700 hover:text-golden transition-colors"
                          data-testid={`clinic-phone-${index}-${phoneIndex}`}
                        >
                          <Phone size={20} className="mr-2 text-golden flex-shrink-0" />
                          {phone}
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map or Additional Info Section */}
      <section className="py-16 golden-gradient">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6" data-testid="contact-cta-heading">
            Visit Us Today
          </h2>
          <p className="text-white text-lg max-w-2xl mx-auto">
            Experience personalized homeopathic care at any of our three convenient locations in Bangalore.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Contact;
